import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Maximize/Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Distribution of Users across TLs Chart for MBF Dashboard
 * Simple pie chart with labels outside
 */
const MBFUserDistributionChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default colors for the pie chart
    const defaultColors = ['#9C7CF4', '#F4A07C', '#D4A84B', '#98D89A', '#7CB8F4', '#FF6B6B', '#4ECDC4', '#45B7D1'];

    // Default data matching the Figma design
    const defaultData = [
        { name: 'Nirma University', percentage: 20.5, color: '#9C7CF4' },
        { name: 'IIT Indore', percentage: 17.6, color: '#F4A07C' },
        { name: 'BITS Pilani', percentage: 13.9, color: '#D4A84B' },
        { name: 'Other', percentage: 22.8, color: '#98D89A' },
        { name: 'IIT B', percentage: 25.2, color: '#7CB8F4' },
    ];

    // Transform API data to chart format
    // API returns: { tl_id, tl_name, user_count, percentage }
    // Chart expects: { name, percentage, color }
    const transformedData = data.length > 0
        ? data.map((item, index) => ({
            name: item.tl_name || item.name || 'Unknown',
            percentage: item.percentage || 0,
            color: item.color || defaultColors[index % defaultColors.length],
        }))
        : defaultData;

    const chartData = transformedData;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            chartInstance.current = new ChartJS(ctx, {
                type: 'pie',
                data: {
                    labels: chartData.map((item) => item.name),
                    datasets: [
                        {
                            data: chartData.map((item) => item.percentage),
                            backgroundColor: chartData.map((item) => item.color),
                            borderColor: '#ffffff',
                            borderWidth: 2,
                            hoverOffset: 5,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: 20,
                    },
                    plugins: {
                        legend: {
                            display: true,
                            position: 'right',
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 15,
                                font: {
                                    family: '"Roboto", sans-serif',
                                    size: 12,
                                },
                                color: '#5c5c5c',
                            },
                        },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => `${context.label}: ${context.raw}%`,
                            },
                        },
                        datalabels: {
                            // Percentages INSIDE the slices
                            color: '#ffffff',
                            font: {
                                size: isMobile ? 11 : 13,
                                weight: '600',
                                family: '"Roboto", sans-serif',
                            },
                            anchor: 'center',
                            align: 'center',
                            formatter: (value) => `${value}%`,
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, data, chartData]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Distribution of users across TLs
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                    minHeight: 0,
                }}
            >
                <canvas ref={chartRef} />
            </Box>
        </Box>
    );
};

export default MBFUserDistributionChart;
